import React, { useEffect, useMemo, useState } from "react";
import { apiGet, apiPost } from "../../services/api";
import { DispenseItem, DrugSearchItem } from "./types";
import { useDebounce } from "../../hooks/useDebounce";

interface PharmacyPanelProps {
    patientIdValue: string;
    showToast: (args: { message: string; type: "success" | "error" | "info" | "warning" }) => void;
}

export default function PharmacyPanel({ patientIdValue, showToast }: PharmacyPanelProps) {
    const [drugQuery, setDrugQuery] = useState("");
    const [drugResults, setDrugResults] = useState<DrugSearchItem[]>([]);
    const [searchingDrugs, setSearchingDrugs] = useState(false);

    const [dispenseItems, setDispenseItems] = useState<DispenseItem[]>([]);
    const [savingDispense, setSavingDispense] = useState(false);
    const [paymentMode, setPaymentMode] = useState<"Cash" | "UPI" | "Card">("Cash");

    const debouncedDrugQuery = useDebounce(drugQuery, 250);

    useEffect(() => {
        const q = debouncedDrugQuery.trim();
        if (q.length < 2) {
            setDrugResults([]);
            setSearchingDrugs(false);
            return;
        }

        const fetchDrugs = async () => {
            setSearchingDrugs(true);
            try {
                const res = await apiGet(`/api/pharmacy/drugs/search?q=${encodeURIComponent(q)}`);
                setDrugResults(Array.isArray(res) ? (res as DrugSearchItem[]) : []);
            } catch {
                setDrugResults([]);
            } finally {
                setSearchingDrugs(false);
            }
        };

        void fetchDrugs();
    }, [debouncedDrugQuery]);

    const dispenseTotal = useMemo(
        () => dispenseItems.reduce((sum, item) => sum + item.price * item.quantity, 0),
        [dispenseItems]
    );

    const handleAddDispenseItem = (drug: DrugSearchItem) => {
        if (!drug.id) return;
        setDispenseItems((prev) => {
            const existing = prev.find((x) => x.id === drug.id);
            if (existing) {
                return prev.map((x) => (x.id === drug.id ? { ...x, quantity: x.quantity + 1 } : x));
            }
            return [
                ...prev,
                {
                    id: drug.id,
                    name: drug.name || "Unknown Drug",
                    price: Number(drug.selling_price || drug.price || 0),
                    quantity: 1,
                    maxQty: drug.stock_qty != null ? drug.stock_qty : drug.stock != null ? drug.stock : null,
                },
            ];
        });
        setDrugQuery("");
        setDrugResults([]);
    };

    const handleDispenseItemDelta = (id: number | string, delta: number) => {
        setDispenseItems((prev) =>
            prev
                .map((x) => {
                    if (x.id === id) {
                        const nextQty = x.quantity + delta;
                        const capped = x.maxQty !== null ? Math.min(nextQty, x.maxQty) : nextQty;
                        return { ...x, quantity: capped };
                    }
                    return x;
                })
                .filter((x) => x.quantity > 0)
        );
    };

    const handleProcessDispense = async () => {
        if (dispenseItems.length === 0) {
            showToast({ message: "No items to dispense.", type: "warning" });
            return;
        }

        const payload = {
            patient_id: patientIdValue,
            bill_type: "Sale",
            items: dispenseItems.map((it) => ({
                id: it.id,
                name: it.name,
                quantity: it.quantity,
                price: it.price,
            })),
        };

        setSavingDispense(true);
        try {
            await apiPost("/api/pharmacy/bill", payload);
            showToast({ message: "Drugs dispensed & billed.", type: "success" });
            setDispenseItems([]);
            setPaymentMode("Cash");
        } catch (e: any) {
            const msg = e?.message || "Failed to dispense drugs.";
            showToast({ message: msg, type: "error" });
        } finally {
            setSavingDispense(false);
        }
    };

    return (
        <div className="mod-standalone-window" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div className="mod-window-header" style={{ borderBottom: 'none', paddingBottom: '0' }}>
                <div>
                    <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        💊 Pharmacy Dispense
                    </h2>
                    <p className="mod-text-muted">Search inventory and bill medications directly to patient.</p>
                </div>
                <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', color: '#c2410c', padding: '6px 16px', borderRadius: '20px', fontWeight: 800, fontSize: '1.1rem' }}>
                    ₹{dispenseTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                </div>
            </div>
            
            <div style={{ padding: '16px 0', position: 'relative', zIndex: 10 }}>
                <input
                    style={{ width: '100%', padding: '14px 16px', border: '1px solid #cbd5e1', borderRadius: '12px', fontSize: '1rem', background: '#f8fafc', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)' }}
                    value={drugQuery}
                    onChange={(e) => setDrugQuery(e.target.value)}
                    placeholder="Search inventory by drug name or composition..."
                />
                {searchingDrugs && <span style={{ position: 'absolute', right: '16px', top: '30px', fontSize: '0.8rem', color: '#64748b' }}>Searching...</span>}
                
                {drugResults.length > 0 && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', boxShadow: 'var(--mod-shadow-hover)', maxHeight: '300px', overflowY: 'auto', marginTop: '4px' }}>
                        {drugResults.map((drug) => (
                            <div key={drug.id} style={{ padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} onClick={() => handleAddDispenseItem(drug)}>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--mod-text-main)' }}>{drug.name}</span>
                                    <small style={{ color: drug.stock_qty === 0 ? '#ef4444' : '#10b981', fontWeight: 600 }}>
                                        {drug.stock_qty === 0 ? 'Out of Stock' : `Available: ${drug.stock_qty ?? drug.stock ?? "N/A"}`}
                                    </small>
                                </div>
                                <strong style={{ fontSize: '1rem', color: '#0369a1' }}>₹{Number(drug.selling_price || drug.price || 0).toLocaleString("en-IN")}</strong>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px', minHeight: '300px' }}>
                {dispenseItems.length === 0 ? (
                    <div className="mod-empty-state" style={{ height: '100%', border: 'none' }}>
                        <div className="mod-empty-icon-wrapper" style={{ background: '#fff7ed', color: '#ea580c' }}>
                            <span className="mod-empty-icon" style={{ opacity: 1 }}>🛒</span>
                        </div>
                        <h3>Cart is Empty</h3>
                        <p>Search for a drug above to start dispensing.</p>
                    </div>
                ) : (
                    dispenseItems.map((item) => (
                        <div key={item.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', boxShadow: 'var(--mod-shadow-sm)' }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--mod-text-main)' }}>{item.name}</div>
                                <div style={{ fontSize: '0.85rem', color: 'var(--mod-text-muted)' }}>₹{item.price.toLocaleString("en-IN", { minimumFractionDigits: 2 })} / unit</div>
                            </div>
                            
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', background: '#f1f5f9', borderRadius: '20px', padding: '4px' }}>
                                    <button style={{ width: '28px', height: '28px', border: 'none', borderRadius: '50%', background: '#fff', color: '#0f172a', fontWeight: 800, cursor: 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }} onClick={() => handleDispenseItemDelta(item.id, -1)}>−</button>
                                    <span style={{ minWidth: '32px', textAlign: 'center', fontSize: '0.9rem', fontWeight: 700 }}>{item.quantity}</span>
                                    <button style={{ width: '28px', height: '28px', border: 'none', borderRadius: '50%', background: '#fff', color: '#0f172a', fontWeight: 800, cursor: 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }} onClick={() => handleDispenseItemDelta(item.id, 1)}>+</button>
                                </div>
                                
                                <div style={{ width: '80px', textAlign: 'right', fontWeight: 800, color: '#0f172a' }}>
                                    ₹{(item.price * item.quantity).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                </div>
                                
                                <button style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '8px', display: 'flex', alignItems: 'center' }} onClick={() => handleDispenseItemDelta(item.id, -item.quantity)}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {dispenseItems.length > 0 && (
                <div style={{ marginTop: 'auto', position: 'sticky', bottom: 0, zIndex: 10, padding: '20px', background: '#ffffff', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 -4px 12px rgba(0,0,0,0.03)' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--mod-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Payment Mode</span>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            {(["Cash", "UPI", "Card"] as const).map((mode) => (
                                <button key={mode} style={{ padding: '8px 16px', borderRadius: '20px', border: paymentMode === mode ? 'none' : '1px solid #cbd5e1', background: paymentMode === mode ? '#fef3c7' : '#fff', color: paymentMode === mode ? '#d97706' : '#64748b', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s' }} onClick={() => setPaymentMode(mode)}>
                                    {mode}
                                </button>
                            ))}
                        </div>
                    </div>
                    
                    <button style={{ padding: '14px 32px', borderRadius: '12px', background: 'linear-gradient(135deg, var(--mod-amber), #ea580c)', color: '#fff', border: 'none', fontWeight: 800, fontSize: '1.05rem', cursor: savingDispense ? 'not-allowed' : 'pointer', opacity: savingDispense ? 0.7 : 1, boxShadow: '0 4px 12px rgba(234, 88, 12, 0.25)', transition: 'transform 0.2s' }} onClick={handleProcessDispense} disabled={savingDispense}>
                        {savingDispense ? "Processing..." : `Checkout ₹${dispenseTotal.toLocaleString("en-IN")}`}
                    </button>
                </div>
            )}
        </div>
    );
}
